import { useState } from 'react'
import { motion } from 'framer-motion'
import cn from 'clsx'

// components
import { Image, useMaxWidth } from '@tracklib/kit'

import styles from './coveranimation.css'

const hidden = { opacity: 0.7, x: '200%', rotate: 12 }
const show = { opacity: 1, x: 0 }

const coverVariants = [
  {
    hidden,
    show: { ...show, rotate: 2, y: -5 },
  },
  {
    hidden,
    show: { ...show, scale: 1.05, y: -5, x: -10 },
  },
  {
    hidden,
    show: { ...show, rotate: -3 },
  },
]

const waveformVariants = [
  {
    hidden: { opacity: 0, y: 50 },
    show: { opacity: 1, y: 0 },
  },
  {
    hidden: { opacity: 0, y: 50 },
    show: { opacity: 1, y: 0 },
  },
]

const mobileCoverVariants = [
  {
    hidden: { opacity: 0.7, rotate: 2, y: -5 },
    show: { opacity: 1, rotate: 2, y: -5 },
  },
  {
    hidden: { opacity: 0.7, scale: 1.05, y: -5, x: -10 },
    show: { opacity: 1, scale: 1.05, y: -5, x: -10 },
  },
  {
    hidden: { opacity: 0.7, rotate: -3 },
    show: { opacity: 1, rotate: -3 },
  },
]

const mobileWaveformVariants = {
  hidden: { opacity: 0 },
  show: { opacity: 1 },
}

type CoverAnimationProps = {
  covers: Array<{ image: number; alt: string }>
  waveforms: Array<{ image: number; alt: string }>
}

const CoverAnimation = ({ covers, waveforms }: CoverAnimationProps): JSX.Element => {
  const [imagesLoaded, setImagesLoaded] = useState(0)
  const isSmall = useMaxWidth()

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: isSmall ? 0 : 0.25,
      },
    },
  }

  return (
    <aside className={styles.covers}>
      <motion.ol variants={container} initial="hidden" animate={imagesLoaded >= covers.length ? 'show' : 'hidden'}>
        {covers.map(({ image, alt }, i) => {
          return (
            <motion.li
              variants={isSmall ? mobileCoverVariants[i] : coverVariants[i]}
              className={cn(styles.cover, styles[`cover${i}`])}
              transition={{ ease: [0.33, 0.52, 0.2, 0.99], duration: isSmall ? 1 : 1.2 }}
              key={image}
            >
              <Image image={image} alt={alt} sizes="25vw" absolute onLoad={() => setImagesLoaded(c => c + 1)} png />
            </motion.li>
          )
        })}
        {waveforms.map(({ image, alt }, i) => (
          <motion.li
            variants={isSmall ? mobileWaveformVariants : waveformVariants[i]}
            className={cn(styles.waveform, styles[`waveform${i}`])}
            transition={{
              type: 'spring',
              duration: isSmall ? 1 : 1.5,
              bounce: 0.1,
              staggerChildren: isSmall ? 0 : 2,
            }}
            key={image}
          >
            <Image image={image} alt={alt} sizes="25vw" />
          </motion.li>
        ))}
      </motion.ol>
    </aside>
  )
}

export default CoverAnimation
